import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useAngelaPage, useRole, API } from "@/App";
import { useVoiceCall } from "@/contexts/VoiceCallProvider";
import { useEntitlements } from "@/hooks/useEntitlements";
import { 
  LayoutDashboard, Layers, 
  BarChart3, Settings,
  Shield, Users,
  MessageSquare,
  LogOut, User, PanelLeftClose, PanelLeft,
  LifeBuoy, CalendarDays, PhoneCall, UserCog, Voicemail, Calculator, Receipt, HardHat, Boxes, Package, KanbanSquare, FileText, FileDiff, Sparkles, Wand2, ClipboardCheck
} from "lucide-react";
import { useI18n } from "@/i18n";

const navKeys = [
  { path: "/", icon: LayoutDashboard, labelKey: "nav.dashboard", section: "pipeline" },
  { path: "/projects", icon: Layers, labelKey: "nav.projects", section: "pipeline" },
  { path: "/crm/leads", icon: KanbanSquare, labelKey: "nav.leads", testId: "sidebar-leads", section: "pipeline" },
  { path: "/calendar", icon: CalendarDays, labelKey: "nav.calendar", section: "pipeline" },
  { path: "/crm/reports", icon: BarChart3, labelKey: "nav.reports", section: "pipeline" },
  { path: "/dialer", icon: PhoneCall, labelKey: "nav.dialer", section: "tools" },
  { path: "/voicemails", icon: Voicemail, labelKey: "nav.voicemail", section: "tools" },
  { path: "/help", icon: LifeBuoy, labelKey: "nav.help", section: "tools" },
];

// Ordered global nav sections. Empty header = no section label.
const GLOBAL_SECTIONS = [
  { key: "pipeline", header: "Pipeline Management" },
  { key: "tools", header: "" },
];

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const role = useRole();
  const canSeeSettings = role === "owner" || role === "manager";
  const { page, projectId } = useAngelaPage();
  const { enabled: callingEnabled } = useVoiceCall();
  const { helpFeedbackEnabled } = useEntitlements();
  const { t } = useI18n();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [projectUnread, setProjectUnread] = useState(0);
  const [coCount, setCoCount] = useState(0);
  const [vmUnread, setVmUnread] = useState(0);
  const menuRef = useRef(null);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    if (path.includes("?")) return false;
    return location.pathname.startsWith(path);
  };

  const isOnProject = page === "project" && projectId;

  // Unread customer replies (SMS/email) for the active project → badge on Contact Manager
  useEffect(() => {
    if (!isOnProject) { setProjectUnread(0); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/projects/${projectId}/unread`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setProjectUnread(data.total || 0);
      } catch { /* non-blocking */ }
    };
    load();
    const interval = setInterval(() => { if (document.visibilityState === "visible") load(); }, 15000);
    const onChange = (e) => {
      if (e?.detail && typeof e.detail.total === "number") setProjectUnread(e.detail.total);
      else load();
    };
    window.addEventListener("contact-unread-changed", onChange);
    return () => { cancelled = true; clearInterval(interval); window.removeEventListener("contact-unread-changed", onChange); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnProject, projectId, token]);

  // Whether the active project has any change orders → toggles the "Change Orders" sidebar item.
  useEffect(() => {
    if (!isOnProject) { setCoCount(0); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/projects/${projectId}/change-orders`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setCoCount((data.change_orders || []).length);
      } catch { /* non-blocking */ }
    };
    load();
    const onChange = () => load();
    window.addEventListener("change-orders-changed", onChange);
    const interval = setInterval(() => { if (document.visibilityState === "visible") load(); }, 30000);
    return () => { cancelled = true; clearInterval(interval); window.removeEventListener("change-orders-changed", onChange); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnProject, projectId, token]);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Poll unread voicemail count for the Voicemail nav badge.
  useEffect(() => {
    if (!callingEnabled || !token) { setVmUnread(0); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/voice/voicemails/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setVmUnread(data.unread || 0);
      } catch { /* non-blocking */ }
    };
    load();
    const interval = setInterval(() => { if (document.visibilityState === "visible") load(); }, 20000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [callingEnabled, token, location.pathname]);

  const navItems = navKeys
    .filter(item => (item.path !== "/dialer" && item.path !== "/voicemails") || callingEnabled)
    .filter(item => item.path !== "/help" || helpFeedbackEnabled)
    .map(item => ({
      ...item,
      label: t(item.labelKey, "web_app"),
      badge: item.path === "/voicemails" ? vmUnread : 0,
    }));

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNavClick = (action) => {
    if (action) action();
    if (onMobileClose) onMobileClose();
  };

  const projectActions = [
    { icon: Wand2, label: "3D Rendering", action: () => { navigate(`/projects/${projectId}?view=renderings`); if (onMobileClose) onMobileClose(); }, color: "text-red-500" },
    { icon: Sparkles, label: "Job Blueprint", action: () => { navigate(`/projects/${projectId}?view=build-scopes`); if (onMobileClose) onMobileClose(); }, color: "text-[#2A9D9D]" },
    { icon: Package, label: "Material Lists", action: () => { navigate(`/projects/${projectId}?view=materials`); if (onMobileClose) onMobileClose(); }, color: "text-[#E9A23B]" },
    { icon: Receipt, label: t("nav.invoices", "web_app"), action: () => { navigate(`/projects/${projectId}?view=invoices`); if (onMobileClose) onMobileClose(); }, color: "text-[#2A9D9D]" },
    { icon: Calculator, label: t("nav.job_costing", "web_app"), action: () => { navigate(`/projects/${projectId}?view=costing`); if (onMobileClose) onMobileClose(); }, color: "text-[#2A9D9D]" },
    { icon: HardHat, label: t("nav.labor", "web_app"), action: () => { navigate(`/projects/${projectId}?view=labor`); if (onMobileClose) onMobileClose(); }, color: "text-[#F59E0B]" },
    { icon: FileDiff, label: "Change Orders", action: () => { navigate(`/projects/${projectId}?view=change-orders`); if (onMobileClose) onMobileClose(); }, color: "text-amber-400", badge: coCount > 0 ? coCount : undefined },
    { icon: Users, label: t("nav.message_crew", "web_app"), action: () => { navigate(`/projects/${projectId}/crew`); if (onMobileClose) onMobileClose(); }, color: "text-blue-400" },
    { icon: FileText, label: "Documents", action: () => { navigate(`/projects/${projectId}?view=documents`); if (onMobileClose) onMobileClose(); }, color: "text-[#7B68EE]" },
  ];

  const clientActions = [
    { icon: ClipboardCheck, label: "Material Review", action: () => { navigate(`/projects/${projectId}?view=material-review`); if (onMobileClose) onMobileClose(); }, color: "text-[#F59E0B]" },
    { icon: MessageSquare, label: t("nav.message_client", "web_app"), action: () => { navigate(`/projects/${projectId}/messenger`); if (onMobileClose) onMobileClose(); }, color: "text-[#2A9D9D]" },
    { icon: UserCog, label: t("nav.contact_manager", "web_app"), action: () => { navigate(`/projects/${projectId}/contact-manager`); if (onMobileClose) onMobileClose(); }, color: "text-[#E45858]", badge: projectUnread },
  ];

  const userName = user?.company_name || user?.email?.split("@")[0] || "User";

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={onMobileClose} data-testid="sidebar-backdrop" />
      )}

      <aside 
        className={`
          fixed left-0 top-0 h-dvh bg-[#080c10] border-r border-white/[0.04] flex flex-col z-50 transition-all duration-300
          ${collapsed ? 'w-16' : 'w-56'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        `} 
        data-testid="sidebar"
      >
      {/* Logo + Collapse Toggle */}
      <div className={`py-4 border-b border-white/[0.04] flex items-center ${collapsed ? 'px-2 justify-center' : 'px-4 justify-between'}`}>
        <Link to="/" className="flex items-center">
          {user?.company_logo ? (
            <img 
              src={user.company_logo} 
              alt={user.company_name || "Company"} 
              className={`transition-all duration-300 object-contain ${collapsed ? 'h-8 w-12' : 'h-12 max-w-[140px]'}`}
            />
          ) : (
            <img 
              src="/logo-white.png" 
              alt="Arq+" 
              className={`transition-all duration-300 ${collapsed ? 'h-6' : 'h-7'}`}
            />
          )}
        </Link>
        {!collapsed && (
          <button
            onClick={onToggle}
            data-testid="collapse-sidebar-btn"
            className="p-1.5 rounded-md text-slate-600 hover:text-slate-300 hover:bg-white/[0.04] transition-all"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="w-4 h-4" />
          </button>
        )}
        {collapsed && (
          <button
            onClick={onToggle}
            data-testid="collapse-sidebar-btn"
            className="p-1.5 rounded-md text-slate-600 hover:text-slate-300 hover:bg-white/[0.04] transition-all absolute top-4 left-1/2 -translate-x-1/2 mt-14"
            title="Expand sidebar"
          >
            <PanelLeft className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className={`flex-1 overflow-y-auto pt-6 px-2 ${collapsed ? 'mt-6' : ''}`}>
        {GLOBAL_SECTIONS.map((sec) => {
          const items = navItems.filter((it) => it.section === sec.key);
          if (items.length === 0) return null;
          return (
            <div key={sec.key} className={sec.header ? "pt-3" : "space-y-0.5"}>
              {!collapsed && sec.header && (
                <p className="text-slate-600 text-[9px] font-bold uppercase tracking-widest px-2.5 mb-1.5">{sec.header}</p>
              )}
              <div className="space-y-0.5">
                {items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onMobileClose}
                    data-testid={item.testId || `nav-${item.label.toLowerCase()}`}
                    title={collapsed ? item.label : undefined}
                    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-200 text-sm ${
                      isActive(item.path)
                        ? "text-[#2A9D9D] bg-[#2A9D9D]/10"
                        : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                    } ${collapsed ? 'justify-center px-0' : ''}`}
                  >
                    <span className="relative shrink-0">
                      <item.icon className="w-4 h-4 shrink-0" />
                      {collapsed && item.badge > 0 && (
                        <span data-testid={`${item.label.toLowerCase()}-badge`}
                          className="absolute -top-1.5 -right-1.5 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-[#080c10]" />
                      )}
                    </span>
                    {!collapsed && <span className="font-normal">{item.label}</span>}
                    {!collapsed && item.badge > 0 && (
                      <span data-testid={`${item.label.toLowerCase()}-badge`}
                        className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold leading-none">
                        {item.badge > 9 ? "9+" : item.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      {/* Project Actions — only when viewing a project */}
      {isOnProject && (
        <div className="pt-2" data-testid="project-actions">
          {!collapsed && <p className="text-slate-600 text-[9px] font-bold uppercase tracking-widest px-2.5 mb-1.5">Project Management</p>}
          <div className="space-y-0.5">
            {projectActions.map((item, i) => (
              <button key={i} onClick={item.action} title={collapsed ? item.label : undefined}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-200 text-sm w-full text-slate-400 hover:text-white hover:bg-white/[0.04] ${collapsed ? 'justify-center px-0' : ''}`}>
                <span className="relative shrink-0">
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                  {collapsed && item.badge > 0 && (
                    <span data-testid={`${item.label.toLowerCase().replace(/\s/g, "-")}-badge`}
                      className="absolute -top-1.5 -right-1.5 w-2 h-2 rounded-full bg-[#E45858] ring-2 ring-[#080c10]" />
                  )}
                </span>
                {!collapsed && <span className="font-medium">{item.label}</span>}
                {!collapsed && item.badge > 0 && (
                  <span data-testid={`${item.label.toLowerCase().replace(/\s/g, "-")}-badge`}
                    className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#E45858] text-white text-[10px] font-bold leading-none">
                    {item.badge > 9 ? "9+" : item.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
          {!collapsed && <p className="text-slate-600 text-[9px] font-bold uppercase tracking-widest px-2.5 mb-1.5 mt-4">Client Management</p>}
          <div className={`space-y-0.5 ${collapsed ? 'mt-3' : ''}`} data-testid="client-actions">
            {clientActions.map((item, i) => (
              <button key={i} onClick={item.action} title={collapsed ? item.label : undefined}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-200 text-sm w-full text-slate-400 hover:text-white hover:bg-white/[0.04] ${collapsed ? 'justify-center px-0' : ''}`}>
                <span className="relative shrink-0">
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                  {collapsed && item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-2 h-2 rounded-full bg-[#E45858] ring-2 ring-[#080c10]" />
                  )}
                </span>
                {!collapsed && <span className="font-medium">{item.label}</span>}
                {!collapsed && item.badge > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#E45858] text-white text-[10px] font-bold leading-none">
                    {item.badge > 9 ? "9+" : item.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      </nav>

      {/* Admin Link */}
      {user?.is_super_admin && (
        <div className="px-2 pb-2">
          <Link
            to="/admin"
            data-testid="admin-panel-link"
            className={`flex items-center gap-2 py-2 px-2.5 rounded-lg text-xs transition-all duration-200 ${
              location.pathname.startsWith('/admin')
                ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                : 'text-red-400/60 hover:text-red-400 hover:bg-red-500/10'
            } ${collapsed ? 'justify-center px-0' : ''}`}
          >
            <Shield className="w-4 h-4 shrink-0" />
            {!collapsed && <span className="uppercase tracking-wider font-medium">{t("nav.admin", "web_app")}</span>}
          </Link>
        </div>
      )}

      {/* User Menu */}
      <div className="relative py-3 px-2 border-t border-white/[0.04]" ref={menuRef}>
        <button
          onClick={() => setUserMenuOpen(!userMenuOpen)}
          data-testid="user-menu-btn"
          className={`flex items-center gap-2.5 w-full py-2 px-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-all duration-200 text-sm ${collapsed ? 'justify-center px-0' : ''}`}
        >
          <div className="w-7 h-7 rounded-full bg-[#2A9D9D]/20 flex items-center justify-center flex-shrink-0">
            <User className="w-3.5 h-3.5 text-[#2A9D9D]" />
          </div>
          {!collapsed && <span className="truncate text-xs font-medium">{userName}</span>}
        </button>

        {/* Popup Menu */}
        {userMenuOpen && (
          <div className={`absolute bottom-full mb-2 ${collapsed ? 'left-2' : 'left-2 right-2'} min-w-[180px] bg-[#11161F] border border-white/[0.08] rounded-xl shadow-2xl py-1.5 z-[60]`}
            data-testid="user-popup-menu">
            <div className="px-3 py-2 border-b border-white/[0.06]">
              <p className="text-white text-xs font-medium truncate">{user?.email}</p>
              {user?.company_name && <p className="text-slate-500 text-[10px] truncate">{user.company_name}</p>}
            </div>
            <button
              onClick={() => { navigate("/profile"); setUserMenuOpen(false); }}
              data-testid="user-menu-profile"
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors"
            >
              <User className="w-3.5 h-3.5" /> My Profile
            </button>
            {canSeeSettings && (
              <button
                onClick={() => { navigate("/settings"); setUserMenuOpen(false); }}
                data-testid="user-menu-settings"
                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors"
              >
                <Settings className="w-3.5 h-3.5" /> {t("nav.settings", "web_app")}
              </button>
            )}
            <button
              onClick={handleLogout}
              data-testid="user-menu-logout"
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-400/70 hover:text-red-400 hover:bg-red-500/[0.06] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> {t("nav.log_out", "web_app")}
            </button>
          </div>
        )}
      </div>
    </aside>
    </>
  );
}
